import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { getLastPageId, CAMP_TO_PAGE_ID, cameFromExternalRedirect, isKorekHeaderCallback } from "./common/utils/lastPage";
import { logger, redactParams } from "./common/utils/logger";
import RouteErrorBoundary from "./common/components/RouteErrorBoundary";

const LandingPageSuccess = lazy(() => import("./pages/default/SuccessPage"));
const LandingPageFailed = lazy(() => import("./pages/default/FailedPage"));
const LandingPageTerms = lazy(() => import("./pages/default/TermsPage"));

const KorekLandingPageHeaderFlow = lazy(() => import("./pages/default/KorekHeaderPage"));
const ZainLandingPageHeaderFlow = lazy(() => import("./pages/default/ZainHeaderPage"));
const AsiaCellLandingPageHeaderFlow = lazy(() => import("./pages/default/AsiaCellHeaderPage"));
const AsiaCellLandingPageConfirmFlow = lazy(() => import("./pages/default/AsiaCellConfirmPage"));
const LandingPagePinFlow = lazy(() => import("./pages/default/PinPage"));

const ZainPage7812 = lazy(() => import("./pages/7812/ZainHeaderPage"));
const KorekPage7812 = lazy(() => import("./pages/7812/KorekHeaderPage"));
const Page7812Pin = lazy(() => import("./pages/7812/PinPage"));
const Page7812Success = lazy(() => import("./pages/7812/SuccessPage"));
const Page7812Failed = lazy(() => import("./pages/7812/FailedPage"));

const ZainPage2231 = lazy(() => import("./pages/2231/ZainHeaderPage"));
const KorekPage2231 = lazy(() => import("./pages/2231/KorekHeaderPage"));
const Page2231Pin = lazy(() => import("./pages/2231/PinPage"));
const Page2231Success = lazy(() => import("./pages/2231/SuccessPage"));
const Page2231Failed = lazy(() => import("./pages/2231/FailedPage"));

const ZainPage6364 = lazy(() => import("./pages/6364/ZainHeaderPage"));
const KorekPage6364 = lazy(() => import("./pages/6364/KorekHeaderPage"));
const Page6364Pin = lazy(() => import("./pages/6364/PinPage"));
const Page6364Success = lazy(() => import("./pages/6364/SuccessPage"));
const Page6364Failed = lazy(() => import("./pages/6364/FailedPage"));

const ZainPage8609 = lazy(() => import("./pages/8609/ZainHeaderPage"));
const KorekPage8609 = lazy(() => import("./pages/8609/KorekHeaderPage"));
const Page8609Pin = lazy(() => import("./pages/8609/PinPage"));
const Page8609Success = lazy(() => import("./pages/8609/SuccessPage"));
const Page8609Failed = lazy(() => import("./pages/8609/FailedPage"));

const ZainPage1802 = lazy(() => import("./pages/1802/ZainHeaderPage"));
const KorekPage1802 = lazy(() => import("./pages/1802/KorekHeaderPage"));
const Page1802Pin = lazy(() => import("./pages/1802/PinPage"));
const Page1802Success = lazy(() => import("./pages/1802/SuccessPage"));
const Page1802Failed = lazy(() => import("./pages/1802/FailedPage"));

const ZainPage3813 = lazy(() => import("./pages/3813/ZainHeaderPage"));
const KorekPage3813 = lazy(() => import("./pages/3813/KorekHeaderPage"));
const Page3813Pin = lazy(() => import("./pages/3813/PinPage"));
const Page3813Success = lazy(() => import("./pages/3813/SuccessPage"));
const Page3813Failed = lazy(() => import("./pages/3813/FailedPage"));

const ZainPage4237 = lazy(() => import("./pages/4237/ZainHeaderPage"));
const KorekPage4237 = lazy(() => import("./pages/4237/KorekHeaderPage"));
const Page4237Pin = lazy(() => import("./pages/4237/PinPage"));
const Page4237TermsCondition = lazy(() => import("./pages/4237/TermsPage"));
const Page4237Success = lazy(() => import("./pages/4237/SuccessPage"));
const Page4237Failed = lazy(() => import("./pages/4237/FailedPage"));

// Korek-only — no ZainHeaderPage for this LP.
const KorekPage8888 = lazy(() => import("./pages/8888/KorekHeaderPage"));
const Page8888Pin = lazy(() => import("./pages/8888/PinPage"));
const Page8888TermsCondition = lazy(() => import("./pages/8888/TermsPage"));
const Page8888Success = lazy(() => import("./pages/8888/SuccessPage"));
const Page8888Failed = lazy(() => import("./pages/8888/FailedPage"));

// Korek-only duplicates of 2231/6364 — neither registers a ZainHeaderPage or
// a dedicated /terms route, same as 8888 and for the same reason (see
// isKorekOnlyPage below).
const KorekPage8526 = lazy(() => import("./pages/8526/KorekHeaderPage"));
const Page8526Pin = lazy(() => import("./pages/8526/PinPage"));
const Page8526Success = lazy(() => import("./pages/8526/SuccessPage"));
const Page8526Failed = lazy(() => import("./pages/8526/FailedPage"));

const KorekPage8759 = lazy(() => import("./pages/8759/KorekHeaderPage"));
const Page8759Pin = lazy(() => import("./pages/8759/PinPage"));
const Page8759Success = lazy(() => import("./pages/8759/SuccessPage"));
const Page8759Failed = lazy(() => import("./pages/8759/FailedPage"));

const zainHePages = {
  "7812": ZainPage7812,
  "2231": ZainPage2231,
  "4237": ZainPage4237,
  "8609": ZainPage8609,
  "6364": ZainPage6364,
  "1802": ZainPage1802,
  "3813": ZainPage3813,
};

const korekHePages = {
  "7812": KorekPage7812,
  "2231": KorekPage2231,
  "4237": KorekPage4237,
  "8609": KorekPage8609,
  "6364": KorekPage6364,
  "1802": KorekPage1802,
  "3813": KorekPage3813,
  "8888": KorekPage8888,
  "8526": KorekPage8526,
  "8759": KorekPage8759,
};

const pinRoutes = [
  { path: "/7812/pin", Component: Page7812Pin },
  { path: "/4237/pin", Component: Page4237Pin },
  { path: "/6364/pin", Component: Page6364Pin },
  { path: "/2231/pin", Component: Page2231Pin },
  { path: "/1802/pin", Component: Page1802Pin },
  { path: "/3813/pin", Component: Page3813Pin },
  { path: "/8609/pin", Component: Page8609Pin },
  { path: "/8888/pin", Component: Page8888Pin },
  { path: "/8526/pin", Component: Page8526Pin },
  { path: "/8759/pin", Component: Page8759Pin },
];

const resultRoutes = [
  { path: "/7812/success", Component: Page7812Success },
  { path: "/7812/failed", Component: Page7812Failed },
  { path: "/2231/success", Component: Page2231Success },
  { path: "/2231/failed", Component: Page2231Failed },
  { path: "/6364/success", Component: Page6364Success },
  { path: "/6364/failed", Component: Page6364Failed },
  { path: "/8609/success", Component: Page8609Success },
  { path: "/8609/failed", Component: Page8609Failed },
  { path: "/1802/success", Component: Page1802Success },
  { path: "/1802/failed", Component: Page1802Failed },
  { path: "/3813/success", Component: Page3813Success },
  { path: "/3813/failed", Component: Page3813Failed },
  { path: "/4237/success", Component: Page4237Success },
  { path: "/4237/failed", Component: Page4237Failed },
  { path: "/8888/success", Component: Page8888Success },
  { path: "/8888/failed", Component: Page8888Failed },
  { path: "/8526/success", Component: Page8526Success },
  { path: "/8526/failed", Component: Page8526Failed },
  { path: "/8759/success", Component: Page8759Success },
  { path: "/8759/failed", Component: Page8759Failed },
];

/**
 * Resolves a human-readable page name for every route this app serves —
 * e.g. "LP 8888 — Korek Header", "LP 7812 — PIN Entry", "Default LP —
 * Success" — so logs read like a product person's description of the page,
 * not a raw path. `/lp/he` is the one ambiguous route (it renders either
 * operator's header for whichever numbered LP the visitor is actually on,
 * decided by pageId/isKorekHeader — both already resolved in App() below),
 * so those are passed in rather than re-derived here.
 */
function resolvePageName(pathname, { pageId, isKorekHeader, isAsiaCellHeader }) {
  const lpLabel = pageId ? `LP ${pageId}` : "Default LP";

  if (pathname === "/lp/he") {
    const operatorLabel = isKorekHeader ? "Korek Header" : isAsiaCellHeader ? "AsiaCell Header" : "Zain Header";
    return `${lpLabel} — ${operatorLabel}`;
  }

  if (pathname === "/lp/confirm") return "Default LP — AsiaCell Confirm";

  const pinMatch = pathname.match(/^\/(\d+)\/pin$/);
  if (pinMatch) return `LP ${pinMatch[1]} — PIN Entry`;
  if (pathname === "/lp/pin") return "Default LP — PIN Entry";

  const successMatch = pathname.match(/^\/(\d+)\/success$/);
  if (successMatch) return `LP ${successMatch[1]} — Success`;
  if (pathname === "/lp/success") return "Default LP — Success";

  const failedMatch = pathname.match(/^\/(\d+)\/failed$/);
  if (failedMatch) return `LP ${failedMatch[1]} — Failed`;
  if (pathname === "/lp/failed") return "Default LP — Failed";

  const termsMatch = pathname.match(/^\/(\d+)\/terms$/);
  if (termsMatch) return `LP ${termsMatch[1]} — Terms & Conditions`;
  if (pathname === "/terms") return "Default LP — Terms & Conditions";

  return pathname;
}

/**
 * Logs a page-view on every route change and sets the ambient page name
 * every subsequent log line will carry (see logger.setPageName) — mounted
 * once inside <Router> so every one of the ~45 page components across all
 * numbered LPs gets tracked without needing its own logging call. Query
 * params are redacted (MSISDN/OTP/etc) before shipping.
 */
function RouteViewLogger({ pageId, isKorekHeader, isAsiaCellHeader }) {
  const location = useLocation();

  useEffect(() => {
    const pageName = resolvePageName(location.pathname, { pageId, isKorekHeader, isAsiaCellHeader });
    logger.setPageName(pageName, location.pathname);

    logger.info("page_view", "route_change", `Visitor opened the "${pageName}" page`, {
      path: location.pathname,
      query: redactParams(Object.fromEntries(new URLSearchParams(location.search))),
    });
  }, [location.pathname, location.search, pageId, isKorekHeader, isAsiaCellHeader]);

  return null;
}

function App({ ti, ts, antiFraudUniqId, camp, isAsiaCellHe }) {
  // Falls back to the page id persisted in localStorage (by
  // public/index.html on the original visit) when the current URL can't
  // resolve one — this is what happens on Korek's Status/Reason callback
  // round-trip back to /lp/he, which carries no campaignId. Without the
  // fallback, the default Header component would render instead of the
  // originally-visited numbered page's, sending failures to the generic
  // /lp/pin instead of that page's own /pin route.
  //
  // Gated behind isKorekHeaderCallback() (exact Status/Reason/ChannelID/
  // ClientID param match — the same shape isAllowedKorekCallback in
  // public/index.html and useHeaderEnrichment.js's redirect-check effect
  // already require) or cameFromExternalRedirect() (referrer-based, a looser
  // fallback signal for that same round-trip when referrer survives but
  // isn't this exact param shape) — otherwise a fresh, campaign-less /lp/he
  // visit (bookmark, manual URL, closed-and-reopened tab) would incorrectly
  // inherit whatever numbered LP this browser last touched. getLastPageId()
  // itself also expires the stored value after MAX_AGE_MS as a second,
  // independent guard.
  const pageId =
    CAMP_TO_PAGE_ID[camp] || (isKorekHeaderCallback() || cameFromExternalRedirect() ? getLastPageId() : null);
  const ZainHeaderComponent = zainHePages[pageId] || ZainLandingPageHeaderFlow;
  const KorekHeaderComponent = korekHePages[pageId] || KorekLandingPageHeaderFlow;

  // True for a page that only ever registered a Korek header component (see
  // the "Korek-only — no ZainHeaderPage for this LP" pages below) — derived
  // from the registries themselves rather than a separately maintained list,
  // so every current and future Korek-only page id is covered automatically.
  const isKorekOnlyPage = Boolean(pageId && korekHePages[pageId] && !zainHePages[pageId]);

  // AsiaCell is default-LP-only per this iteration's scope — never true for
  // a numbered campaign page (pageId truthy), regardless of what network
  // the visitor is actually on, so every numbered page's dispatch below is
  // completely unaffected by this flag.
  const isDefaultAsiaCellHe = Boolean(isAsiaCellHe && !pageId);

  return (
    <Router>
      <RouteViewLogger pageId={pageId} isKorekHeader={Boolean(antiFraudUniqId)} isAsiaCellHeader={isDefaultAsiaCellHe} />
      <RouteErrorBoundary>
      <Suspense fallback={null}>
      <Routes>
        <Route
          path="/lp/he"
          element={
            antiFraudUniqId ? (
              <KorekHeaderComponent antiFraudUniqId={antiFraudUniqId} />
            ) : isKorekOnlyPage ? (
              // No antiFraudUniqId means this visitor either isn't on Korek's
              // network or Korek's own check failed — either way, a Korek-only
              // page must never fall back to rendering ANY Zain component
              // (that component's Subscribe button has no carrier restriction
              // of its own and would happily complete a real Zain
              // subscription). Sending them to this page's own Pin route
              // instead reaches the one flow that does enforce it —
              // usePinFlow's allowedOperators — so a Zain-prefixed number is
              // rejected there instead of silently succeeding here.
              <Navigate to={`/${pageId}/pin${window.location.search}${window.location.hash}`} replace />
            ) : isDefaultAsiaCellHe ? (
              // AsiaCell, default LP only (see isDefaultAsiaCellHe above) —
              // every numbered campaign page (pageId truthy) always falls
              // through to the unchanged Zain branch below, exactly as
              // before this branch existed.
              <AsiaCellLandingPageHeaderFlow />
            ) : (
              <ZainHeaderComponent ti={ti} ts={ts} />
            )
          }
        />

        {/* AsiaCell header flow, step 2 — see AsiaCellHeaderPage.js's doc comment. Default-LP-only, same as /lp/he's AsiaCell branch above. */}
        <Route path="/lp/confirm" element={<AsiaCellLandingPageConfirmFlow />} />

        <Route path="/lp/pin" element={<LandingPagePinFlow />} />

        {pinRoutes.map(({ path, Component }) => (
          <Route
            key={path}
            path={path}
            element={
              <Component
                ti={ti}
                ts={ts}
              />
            }
          />
        ))}

        {resultRoutes.map(({ path, Component }) => (
          <Route key={path} path={path} element={<Component />} />
        ))}

        <Route path="/lp/success" element={<LandingPageSuccess />} />
        <Route path="/lp/failed" element={<LandingPageFailed />} />
        <Route path="/terms" element={<LandingPageTerms />} />
        <Route path="/4237/terms" element={<Page4237TermsCondition />} />
        <Route path="/8888/terms" element={<Page8888TermsCondition />} />
      </Routes>
      </Suspense>
      </RouteErrorBoundary>
    </Router>
  );
}

export default App;
